import { $clientPaymentAccount } from '@app/containers/pages/Client/store/states';
import { Button, Card, Icon, Spinner, Tag } from '@blueprintjs/core';
import Flex from '@components/Flex';
import Heading from '@components/Heading';
import NonIdealState from '@components/NonIdealState';
import Pagination from '@components/Pagination';
import DevText from '@components/Text';
import { InvoiceResource } from 'dy-frontend-http-repository/lib/modules/Invoice/resources';
import repository from 'dy-frontend-http-repository/lib/repository';
import { PriceUtils } from 'dy-frontend-shared/lib/utils';
import { useStore } from 'effector-react';
import moment from 'moment';
import React, { HTMLAttributes, useEffect, useState } from 'react';
import {
    amountOfClientInvoicesOnPage,
    invoiceBillingReasonInformation,
    invoiceStateDescriptionInformation,
    invoiceStateInformation,
} from '../../consts';
import { fetchInvoices } from '../../store/effects';
import { $invoices } from '../../store/states';

export type Props = HTMLAttributes<HTMLDivElement>;

const Invoices: React.FC<Props> = (props) => {
    const clientPaymentAccount = useStore($clientPaymentAccount);
    const clientInvoices = useStore($invoices);
    const isFetchingClientInvoices = useStore(fetchInvoices.pending);

    const [page, setPage] = useState(1);

    const handleLoadInvoicesPage = async (newPage: number) => {
        if (!clientPaymentAccount) return;

        const pageOffset = (newPage - 1) * amountOfClientInvoicesOnPage;

        try {
            await fetchInvoices({
                pagination: {
                    _pagination: { limit: amountOfClientInvoicesOnPage, offset: pageOffset },
                },
                filter: { payment_account_id: clientPaymentAccount.id },
            });
        } catch (e) {
            // TODO: handle error
            console.error(e);
        }
    };

    useEffect(() => {
        handleLoadInvoicesPage(page);

        // eslint-disable-next-line
    }, [page]);

    // Opens or downloads a locally archived invoice PDF (streamed via the authenticated API).
    // If a local PDF is not available or cannot be fetched, keep the legacy Stripe fallback
    // so existing/unarchived invoices behave like production.
    const openInvoicePdf = async (
        invoice: InvoiceResource,
        disposition: 'inline' | 'attachment',
        fallbackUrl: string | null
    ) => {
        // Pre-open a tab synchronously for inline view to avoid pop-up blockers after the async fetch
        const preOpened = disposition === 'inline' ? window.open('about:blank', '_blank') : null;
        const shouldAttemptLocalPdf = invoice.has_local_pdf;

        if (shouldAttemptLocalPdf) {
            try {
                const blob = await repository.invoice().getPdfBlob(invoice.id, disposition);
                const objectUrl = URL.createObjectURL(blob);

                if (disposition === 'attachment') {
                    const anchor = document.createElement('a');
                    anchor.href = objectUrl;
                    anchor.download = `invoice-${invoice.external_id}.pdf`;
                    document.body.appendChild(anchor);
                    anchor.click();
                    anchor.remove();
                } else if (preOpened) {
                    preOpened.location.href = objectUrl;
                } else {
                    window.open(objectUrl, '_blank');
                }

                // Give the browser time to load the document before releasing the object URL
                setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
                return;
            } catch (e) {
                console.error('Local invoice PDF fetch failed', e);
            }
        }

        // Fallback: keep the existing Stripe fallback whenever a local PDF is not available
        // or the local fetch fails. This preserves production behavior for old/unarchived invoices.
        if (fallbackUrl) {
            const openedWindow = window.open(fallbackUrl);
            openedWindow?.focus();
            return;
        }

        // Nothing available at all
        if (preOpened) {
            preOpened.close();
        }
        console.error('Invoice PDF is still being archived locally. Refresh and try View again in about a minute.');
    };

    // Operational visibility for support: shows the local PDF archive status, if known.
    const renderLocalPdfStatus = (invoice: InvoiceResource) => {
        const status = invoice.local_pdf_status;
        if (!status) {
            return null;
        }

        if (status === 'stored') {
            return (
                <Flex className="mt-1" align="center">
                    <Tag icon="cloud-download" intent="success" minimal>
                        Local PDF stored
                    </Tag>
                    {invoice.local_pdf_stored_at && (
                        <DevText muted className="ml-1">
                            {moment(invoice.local_pdf_stored_at).format('D MMMM YYYY, HH:mm')}
                        </DevText>
                    )}
                </Flex>
            );
        }

        if (status === 'pending') {
            return (
                <Flex className="mt-1" align="center">
                    <Tag icon="time" intent="warning" minimal>
                        Local PDF pending
                    </Tag>
                    {typeof invoice.local_pdf_attempts === 'number' && invoice.local_pdf_attempts > 0 && (
                        <DevText muted className="ml-1">
                            Attempts: {invoice.local_pdf_attempts}
                        </DevText>
                    )}
                </Flex>
            );
        }

        // failed
        return (
            <Flex className="mt-1" align="center">
                <Tag icon="error" intent="danger" minimal>
                    Local PDF failed
                </Tag>
                {typeof invoice.local_pdf_attempts === 'number' && (
                    <DevText muted className="ml-1">
                        Attempts: {invoice.local_pdf_attempts}
                    </DevText>
                )}
                {invoice.local_pdf_failure_reason && (
                    <DevText muted className="ml-1">
                        {invoice.local_pdf_failure_reason}
                    </DevText>
                )}
            </Flex>
        );
    };

    const renderInvoice = (invoice: InvoiceResource) => {
        return (
            <Card compact className="mt-2" key={invoice.id}>
                <Flex justify="space-between">
                    <div>
                        <Flex align="center">
                            <Tag className="mr-1">{invoiceStateInformation[invoice.state]}</Tag>

                            <Heading type="h6" className="mr-1">
                                {invoiceBillingReasonInformation[invoice.billing_reason]}
                            </Heading>

                            <DevText muted>
                                {PriceUtils.formatPrice({
                                    price: invoice.total,
                                    shouldDisplayCents: true,
                                    currency: invoice.currency,
                                })}
                            </DevText>
                        </Flex>

                        <DevText muted>{invoiceStateDescriptionInformation[invoice.state]}</DevText>
                        <DevText>
                            {moment(invoice.created_at).format('D MMMM YYYY, HH:mm')} (
                            {moment(invoice.created_at).fromNow()})
                        </DevText>
                        <DevText muted>{invoice.external_id}</DevText>

                        {renderLocalPdfStatus(invoice)}
                    </div>

                    <Flex>
                        {(invoice.has_local_pdf || invoice.pdf_url) && (
                            <Button
                                icon="import"
                                onClick={() => openInvoicePdf(invoice, 'attachment', invoice.pdf_url)}
                            >
                                PDF
                            </Button>
                        )}
                        {(invoice.has_local_pdf || invoice.hosted_url) && (
                            <Button
                                className="ml-1"
                                onClick={() => openInvoicePdf(invoice, 'inline', invoice.hosted_url)}
                            >
                                View
                            </Button>
                        )}
                    </Flex>
                </Flex>
            </Card>
        );
    };

    const renderInvoices = () => {
        // Client invoices were NOT fetched yet
        if (!clientInvoices) {
            return (
                <Flex justify="center">
                    <Spinner />
                </Flex>
            );
        }

        // Client invoices were fetched, but where are no invoices existed for this client
        if (clientInvoices.items.length === 0) {
            return (
                <NonIdealState
                    icon={<Icon className="mb-2" icon="search" size={70} />}
                    title={
                        <Heading type="h4" className="mb-1">
                            No client invoices were found
                        </Heading>
                    }
                />
            );
        }

        return <div>{clientInvoices.items.map(renderInvoice)}</div>;
    };

    const renderPagination = () => {
        // Client brand profiles is not fetched yet
        if (clientInvoices === null) {
            return null;
        }

        // Check if offset paginator exist
        if (clientInvoices.paginator === null) {
            return;
        }

        // Only one page exist
        if (!clientInvoices.paginator.has_more && clientInvoices.paginator.offset === 0) {
            return null;
        }

        return (
            <Flex className="mt-2" justify="flex-end">
                <Pagination
                    fetching={isFetchingClientInvoices}
                    hasMore={clientInvoices.paginator.has_more}
                    page={page}
                    amountOfItemsOnPage={amountOfClientInvoicesOnPage}
                    totalItems={clientInvoices.paginator.total}
                    onPageChange={(newPage) => setPage(newPage)}
                />
            </Flex>
        );
    };

    return (
        <div {...props}>
            <Heading type="h3">Invoices</Heading>

            {renderInvoices()}
            {renderPagination()}
        </div>
    );
};

export default Invoices;
